import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { login, signup, verifyLoginOTP, verifySignupOTP, resendOTP } from '../store/api/auth';
import { setLoginOtpStage, setSignupOtpStage, resetVerificationStage, setToken, setRefreshToken, setUser } from '../store/slices/authSlice';
import { setLoading, setError, setSuccess, startResendCooldown, decrementCooldown } from '../store/slices/uiSlice';
import { loadGuestCartFromStorage } from '../store/slices/cartSlice';
import Loader from '../components/common/loader';

// ─── Validation Schemas ───────────────────────────────────────────────────────

// Indian mobile numbers start with 6, 7, 8, or 9
const INDIAN_PHONE_REGEX = /^[6-9][0-9]{9}$/;
const OTP_REGEX = /^[0-9]{6}$/;

const phoneSchema = Yup.object().shape({
  phone: Yup.string()
    .required('Phone number is required')
    .matches(INDIAN_PHONE_REGEX, 'Enter a valid 10-digit Indian mobile number'),
});

const signupSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name cannot exceed 50 characters'),
  phone: Yup.string()
    .required('Phone number is required')
    .matches(INDIAN_PHONE_REGEX, 'Enter a valid 10-digit Indian mobile number'),
  email: Yup.string()
    .required('Email is required')
    .email('Invalid email format'),
});

const otpSchema = Yup.object().shape({
  otp: Yup.string()
    .required('OTP is required')
    .matches(OTP_REGEX, 'OTP must be exactly 6 digits'),
});

// ─── Input Helpers ────────────────────────────────────────────────────────────

/** Blocks any key that is not a digit (0-9) */
const allowOnlyDigits = (e) => {
  if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
    e.preventDefault();
  }
};

/** Strips non-digits from pasted content and enforces a max length */
const handleDigitPaste = (maxLen) => (e) => {
  e.preventDefault();
  const cleaned = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, maxLen);
  // Use native input setter so React's synthetic onChange fires
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
  nativeInputValueSetter.call(e.target, cleaned);
  e.target.dispatchEvent(new Event('input', { bubbles: true }));
};

// ─── Component ────────────────────────────────────────────────────────────────

const AuthPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, loginStage, signupStage, verificationPhone } = useSelector(state => state.auth);
  const { isLoading, resendCooldown } = useSelector(state => state.ui);
  const [timer, setTimer] = useState(null);
  const [isLogin, setIsLogin] = useState(true);

  // Load guest cart on mount
  useEffect(() => {
    dispatch(loadGuestCartFromStorage());
  }, [dispatch]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  // OTP resend countdown
  useEffect(() => {
    if (resendCooldown > 0 && !timer) {
      const interval = setInterval(() => dispatch(decrementCooldown()), 1000);
      setTimer(interval);
    } else if (resendCooldown === 0 && timer) {
      clearInterval(timer);
      setTimer(null);
    }
    return () => { if (timer) clearInterval(timer); };
  }, [resendCooldown, timer, dispatch]);




 

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleLoginSubmit = async (values, { setSubmitting }) => {
    try {
      dispatch(setLoading(true));
      dispatch(loadGuestCartFromStorage());
      await login({ phone: values.phone });
      dispatch(setLoginOtpStage(values.phone));
      dispatch(setSuccess('OTP sent successfully'));
    } catch (error) {
      dispatch(setError(error.response?.data?.message || 'Failed to send OTP'));
    } finally {
      dispatch(setLoading(false));
      setSubmitting(false);
    }
  };

  const handleSignupSubmit = async (values, { setSubmitting }) => {
    try {
      dispatch(setLoading(true));
      await signup(values);
      dispatch(setSignupOtpStage(values.phone));
      dispatch(setSuccess('OTP sent successfully'));
    } catch (error) {
      dispatch(setError(error.response?.data?.message || 'Failed to create account'));
    } finally {
      dispatch(setLoading(false));
      setSubmitting(false);
    }
  };

  const handleLoginOtpSubmit = async (values, { setSubmitting }) => {
    try {
      dispatch(setLoading(true));
      dispatch(loadGuestCartFromStorage());
      const result = await verifyLoginOTP({ phone: verificationPhone, otp: values.otp });
      dispatch(setToken(result.data.token));
      dispatch(setRefreshToken(result.data.refreshToken));
      dispatch(setUser(result.data.user));
      dispatch(setSuccess('Login successful'));
      dispatch(resetVerificationStage());
      setTimeout(() => navigate('/'), 1000);
    } catch (error) {
      dispatch(setError(error.response?.data?.message || 'Invalid OTP'));
    } finally {
      dispatch(setLoading(false));
      setSubmitting(false);
    }
  };

  const handleSignupOtpSubmit = async (values, { setSubmitting }) => {
    try {
      dispatch(setLoading(true));
      const result = await verifySignupOTP({ phone: verificationPhone, otp: values.otp });
      dispatch(setToken(result.data.token));
      dispatch(setUser(result.data.user));
      dispatch(setSuccess('Registration completed successfully'));
      dispatch(resetVerificationStage());
      navigate('/');
    } catch (error) {
      dispatch(setError(error.response?.data?.message || 'Invalid OTP'));
    } finally {
      dispatch(setLoading(false));
      setSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    try {
      dispatch(setLoading(true));
      await resendOTP({ phone: verificationPhone, type: isLogin ? 'login' : 'signup' });
      dispatch(startResendCooldown());
      dispatch(setSuccess('OTP resent successfully'));
    } catch (error) {
      dispatch(setError(error.response?.data?.message || 'Failed to resend OTP'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleGoBack = () => dispatch(resetVerificationStage());

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    dispatch(resetVerificationStage());
  };

  const showOtpForm = (isLogin && loginStage === 'otp') || (!isLogin && signupStage === 'otp');

  useEffect(() => {
  if (showOtpForm) {
    setTimeout(() => {
      const otpInput = document.querySelector('input[name="otp"]');
      if (otpInput) otpInput.value = '';
    }, 100);
  }
}, [showOtpForm]);

  // ── Shared field props ───────────────────────────────────────────────────────

  const phoneFieldProps = {
  type: 'text',
  inputMode: 'numeric',
  maxLength: 10,
  onKeyDown: allowOnlyDigits,
  onPaste: handleDigitPaste(10),
  autoComplete: 'off',
};

  const otpFieldProps = {
  type: 'text',
  inputMode: 'numeric',
  maxLength: 6,
  onKeyDown: allowOnlyDigits,
  onPaste: handleDigitPaste(6),
  autoComplete: 'off',        // ✅ hard off
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: false,
};

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 p-4">
      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden" style={{ minHeight: '500px' }}>

        {/* Logo */}
        <div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-20">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
            <img src="/assets/logo/klm-logo.png" alt="KLM KART" className="w-12 h-12" />
          </div>
        </div>

        <div className="flex relative h-full" style={{ minHeight: '500px' }}>

          {/* ── Login Panel ─────────────────────────────────────────────────── */}
          <div
            className={`w-1/2 p-12 pt-24 transition-all duration-500 ${
              isLogin ? 'opacity-100 pointer-events-auto' : 'opacity-30 pointer-events-none'
            }`}
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">LOG IN</h2>

            {!showOtpForm || !isLogin ? (
              /* Phone entry */
              <Formik initialValues={{ phone: '' }} validationSchema={phoneSchema} onSubmit={handleLoginSubmit}>
                {({ isSubmitting }) => (
                  <Form className="space-y-6">
                    <div>
                      <Field
                        {...phoneFieldProps}
                        name="phone"
                        className="w-full px-4 py-3 bg-gray-50 border-b-2 border-gray-300 focus:border-red-500 outline-none transition-colors"
                        placeholder="Phone Number"
                      />
                      <ErrorMessage name="phone" component="div" className="text-red-500 text-sm mt-1" />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-full transition-colors shadow-md"
                      disabled={isSubmitting || isLoading}
                    >
                      {isLoading ? (
                        <div className="flex justify-center items-center">
                          <Loader size="sm" color="white" />
                          <span className="ml-2">Sending...</span>
                        </div>
                      ) : 'SEND OTP'}
                    </button>

                    <div className="text-center mt-6">
                      <button type="button" className="text-red-600 hover:underline text-sm">
                        Forgot password?
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            ) : (
              /* OTP entry */
              <Formik key="login-otp" initialValues={{ otp: '' }} validationSchema={otpSchema} onSubmit={handleLoginOtpSubmit}>

                {({ isSubmitting }) => (
                  <Form className="space-y-6">
                    <div className="text-center mb-4">
                      <p className="text-gray-600 text-sm">
                        Enter OTP sent to<br />
                        <span className="font-semibold">{verificationPhone}</span>
                      </p>
                    </div>

                    <div>
                      <Field
                        {...otpFieldProps}
                        name="otp"
                        className="w-full px-4 py-3 bg-gray-50 border-b-2 border-gray-300 focus:border-red-500 outline-none transition-colors text-center text-lg tracking-widest"
                        placeholder="Enter 6-digit OTP"
                      />
                      <ErrorMessage name="otp" component="div" className="text-red-500 text-sm mt-1 text-center" />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-full transition-colors shadow-md"
                      disabled={isSubmitting || isLoading}
                    >
                      {isLoading ? (
                        <div className="flex justify-center items-center">
                          <Loader size="sm" color="white" />
                          <span className="ml-2">Verifying...</span>
                        </div>
                      ) : 'VERIFY OTP'}
                    </button>

                    <div className="flex justify-between items-center text-sm mt-4">
                      <button type="button" className="text-red-600 hover:underline" onClick={handleGoBack}>
                        Change Number
                      </button>
                      <button
                        type="button"
                        className={`text-red-600 hover:underline ${resendCooldown > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={handleResendOTP}
                        disabled={resendCooldown > 0}
                      >
                        {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend OTP'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>

          {/* ── Sliding Divider ─────────────────────────────────────────────── */}
          <div
            className={`absolute top-0 w-1/2 h-full bg-gradient-to-br from-red-500 to-pink-600 transition-all duration-500 ease-in-out z-10 flex items-center justify-center ${
              isLogin ? 'left-1/2 rounded-l-[50px]' : 'left-0 rounded-r-[50px]'
            }`}
          >
            <div className="text-center text-white px-8">
              <h3 className="text-3xl font-bold mb-4">
                {isLogin ? 'Hello, Friend!' : 'Welcome Back!'}
              </h3>
              <p className="mb-6 text-sm opacity-90">
                {isLogin
                  ? 'Enter your details and start your journey with us'
                  : 'To keep connected with us please login with your personal info'}
              </p>
              <button
                onClick={toggleAuthMode}
                className="bg-white text-red-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                {isLogin ? 'SIGN UP' : 'LOG IN'}
              </button>
            </div>
          </div>

          {/* ── Signup Panel ─────────────────────────────────────────────────── */}
          <div
            className={`w-1/2 p-12 pt-24 transition-all duration-500 ${
              !isLogin ? 'opacity-100 pointer-events-auto' : 'opacity-30 pointer-events-none'
            }`}
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">SIGN UP</h2>

            {!showOtpForm || isLogin ? (
              /* Signup form */
              <Formik
                initialValues={{ name: '', phone: '', email: '' }}
                validationSchema={signupSchema}
                onSubmit={handleSignupSubmit}
              >
                {({ isSubmitting }) => (
                  <Form className="space-y-4">
                    <div>
                      <Field
                        type="text"
                        name="name"
                        className="w-full px-4 py-3 bg-gray-50 border-b-2 border-gray-300 focus:border-pink-500 outline-none transition-colors"
                        placeholder="Full Name"
                      />
                      <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
                    </div>

                    <div>
                      <Field
                        type="email"
                        name="email"
                         autoComplete="email" 
                        className="w-full px-4 py-3 bg-gray-50 border-b-2 border-gray-300 focus:border-pink-500 outline-none transition-colors"
                        placeholder="Email Address"
                      />
                      <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
                    </div>

                    <div>
                      <Field
                        {...phoneFieldProps}
                        name="phone"
                        className="w-full px-4 py-3 bg-gray-50 border-b-2 border-gray-300 focus:border-pink-500 outline-none transition-colors"
                        placeholder="Phone Number"
                      />
                      <ErrorMessage name="phone" component="div" className="text-red-500 text-sm mt-1" />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 rounded-full transition-colors shadow-md mt-6"
                      disabled={isSubmitting || isLoading}
                    >
                      {isLoading ? (
                        <div className="flex justify-center items-center">
                          <Loader size="sm" color="white" />
                          <span className="ml-2">Creating...</span>
                        </div>
                      ) : 'SIGN UP'}
                    </button>
                  </Form>
                )}
              </Formik>
            ) : (
              /* Signup OTP */
             <Formik key="signup-otp" initialValues={{ otp: '' }} validationSchema={otpSchema} onSubmit={handleSignupOtpSubmit}>

                {({ isSubmitting }) => (
                  <Form className="space-y-6">
                    <div className="text-center mb-4">
                      <p className="text-gray-600 text-sm">
                        Enter OTP sent to<br />
                        <span className="font-semibold">{verificationPhone}</span>
                      </p>
                    </div>

                    <div>
                      <Field
                        {...otpFieldProps}
                        name="otp"
                        className="w-full px-4 py-3 bg-gray-50 border-b-2 border-gray-300 focus:border-pink-500 outline-none transition-colors text-center text-lg tracking-widest"
                        placeholder="Enter 6-digit OTP"
                      />
                      <ErrorMessage name="otp" component="div" className="text-red-500 text-sm mt-1 text-center" />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 rounded-full transition-colors shadow-md"
                      disabled={isSubmitting || isLoading}
                    >
                      {isLoading ? (
                        <div className="flex justify-center items-center">
                          <Loader size="sm" color="white" />
                          <span className="ml-2">Verifying...</span>
                        </div>
                      ) : 'VERIFY OTP'}
                    </button>

                    <div className="flex justify-between items-center text-sm mt-4">
                      <button type="button" className="text-pink-600 hover:underline" onClick={handleGoBack}>
                        Change Details
                      </button>
                      <button
                        type="button"
                        className={`text-pink-600 hover:underline ${resendCooldown > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={handleResendOTP}
                        disabled={resendCooldown > 0}
                      >
                        {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend OTP'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default AuthPage;